<template>
	<div class="paragraph-dialog-backdrop" @mousedown.self="handleBackdropClick">
		<div class="paragraph-dialog" @mousedown.stop>
			<!-- Заголовок -->
			<div class="dialog-header">
				<h3>Абзац</h3>
				<div class="dialog-header-actions">
					<button class="dialog-help-btn" title="Справка">?</button>
					<button class="dialog-close-btn" @click="$emit('close')">✕</button>
				</div>
			</div>

			<!-- Контент -->
			<div class="dialog-content">
				<!-- Отступы -->
				<div class="form-section">
					<h4 class="section-title">Отступы</h4>
					<div class="form-grid">
						<div class="form-field">
							<label>Слева:</label>
							<div class="input-with-unit">
								<input 
									type="number" 
									v-model.number="settings.indentLeft" 
									step="0.1"
									min="0"
								/>
								<select v-model="settings.indentLeftUnit">
									<option value="cm">см</option>
									<option value="pt">пт</option>
								</select>
							</div>
						</div>
						<div class="form-field">
							<label>Справа:</label>
							<div class="input-with-unit">
								<input 
									type="number" 
									v-model.number="settings.indentRight" 
									step="0.1"
									min="0"
								/>
								<select v-model="settings.indentRightUnit">
									<option value="cm">см</option>
									<option value="pt">пт</option>
								</select>
							</div>
						</div>
					</div>
					<div class="form-row-special">
						<div class="form-field-inline">
							<label>Первая строка:</label>
							<select class="select-medium" v-model="settings.firstLineType">
								<option value="none">Нет</option>
								<option value="indent">Отступ</option>
								<option value="hanging">Выступ</option>
							</select>
						</div>
						<div v-if="settings.firstLineType !== 'none'" class="input-with-unit input-small">
							<input 
								type="number" 
								v-model.number="settings.firstLineValue" 
								step="0.1"
								min="0"
							/>
							<select v-model="settings.firstLineUnit">
								<option value="cm">см</option>
								<option value="pt">пт</option>
							</select>
						</div>
					</div>
				</div>

				<!-- Образец -->
				<div class="form-section preview-section">
					<h4 class="section-title">Образец</h4>
					<div class="preview-container">
						<div class="preview-content">
							<div class="preview-paragraph preview-paragraph--prev">
								Предыдущий абзац. Предыдущий абзац. Предыдущий абзац. Предыдущий абзац. Предыдущий абзац.
							</div>
							<div class="preview-paragraph preview-paragraph--current" :style="currentParagraphStyle">
								<div class="preview-ruler preview-ruler--top"></div>
								Текущий абзац. Текущий абзац. Текущий абзац. Текущий абзац. Текущий абзац. Текущий абзац. Текущий абзац. Текущий абзац.
								<div class="preview-ruler preview-ruler--bottom"></div>
							</div>
							<div class="preview-paragraph preview-paragraph--next">
								Следующий абзац. Следующий абзац. Следующий абзац. Следующий абзац. Следующий абзац.
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Кнопки действий -->
			<div class="dialog-actions">
				<button class="dialog-btn dialog-btn--primary" @click="apply">ОК</button>
				<button class="dialog-btn" @click="$emit('close')">Отмена</button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { reactive, computed } from 'vue'

const props = defineProps({
	initialSettings: {
		type: Object,
		default: () => ({})
	}
})

const emit = defineEmits(['close', 'apply'])

const settings = reactive({
	// Отступы
	indentLeft: 0,
	indentLeftUnit: 'cm',
	indentRight: 0,
	indentRightUnit: 'cm',
	firstLineType: 'none',
	firstLineValue: 0,
	firstLineUnit: 'cm',
	mirrorIndents: false,
	
	// Интервалы
	spacingBefore: 0,
	spacingBeforeUnit: 'pt',
	spacingAfter: 0,
	spacingAfterUnit: 'pt',
	lineSpacingType: 'single',
	lineSpacingValue: 1,
	noSpacingBetweenSameStyle: false,
	
	...props.initialSettings
})

// Конвертация единиц в px для превью
const toPx = (value, unit) => {
	if (unit === 'cm') return value * 37.8 // 1cm ≈ 37.8px
	if (unit === 'pt') return value * 1.33 // 1pt ≈ 1.33px
	return value
}

// Live-превью текущего абзаца
const currentParagraphStyle = computed(() => {
	const leftPx = toPx(settings.indentLeft, settings.indentLeftUnit)
	const rightPx = toPx(settings.indentRight, settings.indentRightUnit)
	const beforePx = toPx(settings.spacingBefore, settings.spacingBeforeUnit)
	const afterPx = toPx(settings.spacingAfter, settings.spacingAfterUnit)
	
	let firstLineIndent = 0
	if (settings.firstLineType === 'indent') {
		firstLineIndent = toPx(settings.firstLineValue, settings.firstLineUnit)
	} else if (settings.firstLineType === 'hanging') {
		firstLineIndent = -toPx(settings.firstLineValue, settings.firstLineUnit)
	}
	
	let lineHeight = 1.15
	if (settings.lineSpacingType === 'single') lineHeight = 1
	else if (settings.lineSpacingType === '1.15') lineHeight = 1.15
	else if (settings.lineSpacingType === '1.5') lineHeight = 1.5
	else if (settings.lineSpacingType === 'double') lineHeight = 2
	else if (settings.lineSpacingType === 'multiple') lineHeight = settings.lineSpacingValue
	else if (settings.lineSpacingType === 'exact') lineHeight = `${toPx(settings.lineSpacingValue, 'pt')}px`
	else if (settings.lineSpacingType === 'minimum') lineHeight = Math.max(1, toPx(settings.lineSpacingValue, 'pt') / 16)
	
	return {
		marginLeft: `${leftPx}px`,
		marginRight: `${rightPx}px`,
		textIndent: `${firstLineIndent}px`,
		marginTop: `${beforePx}px`,
		marginBottom: `${afterPx}px`,
		lineHeight: lineHeight
	}
})

const apply = () => {
	emit('apply', { ...settings })
	emit('close')
}

const handleBackdropClick = () => {
	// Не закрывать по клику на backdrop
}
</script>

<style scoped>
.paragraph-dialog-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10000;
}

.paragraph-dialog {
	background: #fff;
	border-radius: 0;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
	width: 520px;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Заголовок */
.dialog-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px;
	background: #f0f0f0;
	border-bottom: 1px solid #d0d0d0;
}

.dialog-header h3 {
	margin: 0;
	font-size: 13px;
	font-weight: 600;
	color: #000;
}

.dialog-header-actions {
	display: flex;
	gap: 4px;
}

.dialog-help-btn,
.dialog-close-btn {
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	cursor: pointer;
	font-size: 14px;
	color: #333;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 2px;
}

.dialog-help-btn:hover,
.dialog-close-btn:hover {
	background: #e0e0e0;
}

/* Контент */
.dialog-content {
	flex: 1;
	overflow-y: auto;
	padding: 12px;
}

.form-section {
	border: 1px solid #d0d0d0;
	padding: 10px;
	background: #fafafa;
	margin-bottom: 10px;
}

.section-title {
	margin: 0 0 10px 0;
	font-size: 12px;
	font-weight: 600;
	color: #333;
}

.form-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
	margin-bottom: 8px;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.form-field label {
	font-size: 11px;
	color: #666;
}

.form-row-special {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
	flex-wrap: wrap;
}

.form-field-inline {
	display: flex;
	align-items: center;
	gap: 4px;
}

.form-field-inline label {
	font-size: 11px;
	color: #666;
	white-space: nowrap;
}

.input-with-unit {
	display: flex;
	gap: 2px;
}

.input-with-unit input {
	flex: 1;
	padding: 4px 6px;
	border: 1px solid #a0a0a0;
	font-size: 12px;
	min-width: 0;
	width: 60px;
}

.input-with-unit.input-small input {
	width: 50px;
}

.input-with-unit select {
	width: 45px;
	padding: 4px 2px;
	border: 1px solid #a0a0a0;
	font-size: 11px;
	background: #fff;
}

.select-medium {
	padding: 4px 6px;
	border: 1px solid #a0a0a0;
	font-size: 12px;
	background: #fff;
	min-width: 120px;
}

.unit-label {
	padding: 4px 6px;
	background: #e8e8e8;
	border: 1px solid #a0a0a0;
	font-size: 11px;
	color: #333;
	display: flex;
	align-items: center;
}

.form-checkbox {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 6px;
}

.form-checkbox input[type="checkbox"] {
	margin: 0;
	width: 14px;
	height: 14px;
}

.form-checkbox label {
	font-size: 11px;
	color: #333;
	cursor: pointer;
	user-select: none;
}

/* Превью */
.preview-section {
	background: #fff;
}

.preview-container {
	border: 1px solid #d0d0d0;
	padding: 12px;
	background: #fff;
	min-height: 140px;
}

.preview-content {
	font-size: 10px;
	color: #666;
	line-height: 1.3;
}

.preview-paragraph {
	margin: 0;
	padding: 0;
	text-align: justify;
}

.preview-paragraph--prev,
.preview-paragraph--next {
	margin: 4px 0;
}

.preview-paragraph--current {
	color: #000;
	position: relative;
	background: #f9f9f9;
}

.preview-ruler {
	position: absolute;
	left: 0;
	right: 0;
	height: 1px;
	background: #0078d4;
}

.preview-ruler--top {
	top: 0;
}

.preview-ruler--bottom {
	bottom: 0;
}

/* Кнопки действий */
.dialog-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 10px 12px;
	border-top: 1px solid #d0d0d0;
	background: #f0f0f0;
}

.dialog-btn {
	padding: 6px 20px;
	border: 1px solid #a0a0a0;
	background: #fff;
	font-size: 12px;
	cursor: pointer;
	border-radius: 2px;
	min-width: 75px;
}

.dialog-btn:hover {
	background: #f0f0f0;
	border-color: #0078d4;
}

.dialog-btn--primary {
	background: #0078d4;
	color: #fff;
	border-color: #0078d4;
}

.dialog-btn--primary:hover {
	background: #005a9e;
	border-color: #005a9e;
}

/* Скроллбар */
.dialog-content::-webkit-scrollbar {
	width: 10px;
}

.dialog-content::-webkit-scrollbar-track {
	background: #f0f0f0;
}

.dialog-content::-webkit-scrollbar-thumb {
	background: #c0c0c0;
	border-radius: 5px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
	background: #a0a0a0;
}
</style>
